import React, { useCallback } from 'react';

import { AmendBookingCheckoutNavigator } from '@/booking-amendment/interfaces';
import { ResponsiveConditional } from '@/common';
import { DEFAULT_TRANSLATION_STRINGS, useTranslation } from '@/i18n';
import { Header, HeaderNav } from '@/ui/layout';
import { WizardStep } from '@/ui/wizard';

type StepKey =
  | 'amend-booking'
  | 'select-room'
  | 'compare-changes'
  | 'additional-information'
  | 'payment'
  | 'confirmation';

type WizardDictionaryKey =
  keyof typeof DEFAULT_TRANSLATION_STRINGS['en-GB']['bookingManagement']['wizard'];

const stepTranslationKey = (
  key: StepKey
): keyof typeof DEFAULT_TRANSLATION_STRINGS['en-GB']['bookingManagement']['wizard'] => {
  const map: Record<StepKey, WizardDictionaryKey> = {
    'amend-booking': 'amendBookingTitle',
    'select-room': 'roomSelectionTitle',
    'compare-changes': 'compareChangesTitle',
    'additional-information': 'additionalInfoTitle',
    payment: 'paymentTitle',
    confirmation: 'confirmationTitle',
  };

  return map[key];
};

interface BookingAmendmentWizardProps {
  navigator: AmendBookingCheckoutNavigator;
  activeStep:
    | 'amend-booking'
    | 'select-room'
    | 'compare-changes'
    | 'additional-information'
    | 'payment'
    | 'confirmation';
}

export const BookingAmendmentWizard: React.FC<BookingAmendmentWizardProps> = ({
  activeStep,
  navigator,
}) => {
  const { t } = useTranslation('bookingManagement');

  const getTranslationForStepKey = useCallback(
    (key: StepKey) => {
      return t(`wizard.${stepTranslationKey(key)}` as any);
    },
    [t]
  );

  return (
    <Header>
      <ResponsiveConditional
        breakpoint={1200}
        desktop={
          <HeaderNav>
            <WizardStep
              stepNumber={1}
              title={getTranslationForStepKey('amend-booking')}
              onClick={navigator.manageBookingPage}
              isActive={activeStep === 'amend-booking'}
            />
            <WizardStep
              stepNumber={2}
              title={getTranslationForStepKey('select-room')}
              onClick={navigator.searchResultsPage}
              isActive={activeStep === 'select-room'}
            />
            <WizardStep
              stepNumber={3}
              title={getTranslationForStepKey('compare-changes')}
              onClick={navigator.compareChangesPage}
              isActive={activeStep === 'compare-changes'}
            />
            <WizardStep
              stepNumber={4}
              title={getTranslationForStepKey('additional-information')}
              onClick={navigator.additionalInformationPage}
              isActive={activeStep === 'additional-information'}
            />
            <WizardStep
              stepNumber={5}
              title={getTranslationForStepKey('payment')}
              onClick={navigator.paymentPage}
              isActive={activeStep === 'payment'}
            />
          </HeaderNav>
        }
        mobile={
          <HeaderNav>
            <WizardStep title={getTranslationForStepKey(activeStep)} />
          </HeaderNav>
        }
      />
    </Header>
  );
};
